/**
 * features/notifications/entityRoute.ts
 *
 * Resolver INTERNO de navegación para el centro de notificaciones (N2).
 *
 * Traduce (entity_type, entity_id) → ruta INTERNA de la app EXCLUSIVAMENTE por un
 * mapa permitido y cerrado. NUNCA usa una URL recibida de la base, ni `action_url`,
 * ni rutas externas: sólo compone rutas propias a partir de un id validado. La
 * autorización final la siguen imponiendo el router (RequirePermission + storeGate),
 * los permisos y el RLS del servidor; este resolver sólo decide "a dónde apuntar".
 *
 * Ampliable: para habilitar una entidad nueva (garantías, traspasos, créditos,
 * inventario, tareas, …) basta añadir una entrada a ENTITY_ROUTES con su builder.
 * NO se habilitan aquí todavía: sólo `repair`.
 */

/** Sólo aceptamos ids con forma de UUID: defensa contra valores arbitrarios en la URL. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Builder de ruta interna a partir de un id ya validado (forma UUID). */
type EntityRouteBuilder = (id: string) => string

/**
 * Mapa interno PERMITIDO. Clave = entity_type canónico de N2 (singular, tal como
 * lo emite el servidor). Cada builder compone una ruta propia y codifica el id.
 */
const ENTITY_ROUTES: Record<string, EntityRouteBuilder> = {
  repair: id => `/repairs?repair=${encodeURIComponent(id)}`,
  // Próximas entidades (NO habilitadas en esta corrida):
  // warranty:  id => `/warranties?warranty=${encodeURIComponent(id)}`,
  // transfer:  id => `/transfers?transfer=${encodeURIComponent(id)}`,
  // credit:    id => `/credit?account=${encodeURIComponent(id)}`,
  // inventory: id => `/inventory?item=${encodeURIComponent(id)}`,
  // task:      id => `/tasks?task=${encodeURIComponent(id)}`,
}

/**
 * Devuelve la ruta interna para abrir la entidad de una notificación, o `null` si
 * el tipo no está mapeado, no hay id, o el id no tiene forma de UUID. `null` = no
 * navegar (la campana simplemente marca leída, sin romper la UI).
 */
export function resolveEntityRoute(
  entityType: string | null | undefined,
  entityId: string | null | undefined
): string | null {
  if (!entityType || !entityId) return null
  // Sólo propiedades PROPIAS del allowlist: nunca resolver miembros heredados de
  // Object.prototype (toString/constructor/valueOf/hasOwnProperty/__proto__…), que
  // podrían devolver una ruta basura o lanzar. Un entity_type desconocido ⇒ null.
  if (!Object.prototype.hasOwnProperty.call(ENTITY_ROUTES, entityType)) return null
  const build = ENTITY_ROUTES[entityType]
  if (!UUID_RE.test(entityId)) return null
  return build(entityId)
}
